import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import oddsDataService from '../../utils/services/oddsDataService';
import resultsTrackerService from '../../utils/services/resultsTrackerService';
import AddToTrackerModal from './AddToTrackerModal';
import FiltersModal from './FiltersModal';
import Toast from '../Toast/Toast';

// Import bookmaker logos
import sportsbetLogo from '../../assets/sb.png';
import tabLogo from '../../assets/tab.jpg';
import pointsbetLogo from '../../assets/pb.png';
import nedsLogo from '../../assets/neds.jpg';
import betrightLogo from '../../assets/betright.jpg';
import bet365Logo from '../../assets/bet365.png';
import betmakersLogo from '../../assets/betmakers.png';
import ladbrokesLogo from '../../assets/ladbrokes.webp';
import fanduelLogo from '../../assets/fanduel.jpg';
import draftkingsLogo from '../../assets/draftkings.jpg';
import betmgmLogo from '../../assets/betmgm.png';
import dabbleLogo from '../../assets/dabble.webp';
import pinnacleLogo from '../../assets/pn_logo.jpeg';

const SPORT_OPTIONS = [
    { value: 'nba', label: 'NBA' },
];

const STRATEGY_OPTIONS = [
    { value: 'player_line', label: 'EV' },
    { value: 'market_value', label: 'Market Value' },
    { value: 'sb_baseline', label: 'Market Value - SB Baseline' },
    { value: 'arbs', label: 'Arbs' },
];

const MV_STRATEGIES = new Set(['market_value', 'sb_baseline']);

const DEVIG_OPTIONS = [
    { value: 'snipeaio', label: 'SnipeAIO' },
    { value: 'sportsbet', label: 'Sportsbet' },
    { value: 'draftkings', label: 'DraftKings' },
    { value: 'pinnacle', label: 'Pinnacle' },
    { value: 'sb_dk', label: 'SB+DK' },
    { value: 'sb_dk_pn', label: 'SB+DK+PN' },
];

const COMBO_DEVIG_BOOKMAKERS = {
    sb_dk: ['sportsbet', 'draftkings'],
    sb_dk_pn: ['sportsbet', 'draftkings', 'pinnacle'],
};

const US_BOOKMAKERS = new Set(['fanduel', 'draftkings', 'betmgm', 'pinnacle']);

const BOOKMAKER_LOGOS = {
    'sportsbet': sportsbetLogo,
    'tab': tabLogo,
    'pointsbet': pointsbetLogo,
    'neds': nedsLogo,
    'betright': betrightLogo,
    'bet365': bet365Logo,
    'betmakers': betmakersLogo,
    'ladbrokes': ladbrokesLogo,
    'fanduel': fanduelLogo,
    'draftkings': draftkingsLogo,
    'betmgm': betmgmLogo,
    'dabble': dabbleLogo,
    'pinnacle': pinnacleLogo,
};

const BOOKMAKER_DISPLAY_NAMES = {
    'sportsbet': 'Sportsbet',
    'tab': 'TAB',
    'pointsbet': 'Pointsbet',
    'neds': 'Neds',
    'betright': 'Betright',
    'bet365': 'Bet365',
    'betmakers': 'Betmakers',
    'ladbrokes': 'Ladbrokes',
    'fanduel': 'FanDuel',
    'draftkings': 'DraftKings',
    'betmgm': 'BetMGM',
    'dabble': 'Dabble',
    'pinnacle': 'Pinnacle',
};

function formatStartTime(isoString) {
    const date = new Date(isoString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const isTomorrow = date.toDateString() === tomorrow.toDateString();

    const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    if (isToday) return `Today ${timeStr}`;
    if (isTomorrow) return `Tomorrow ${timeStr}`;

    const dateStr = date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    return `${dateStr} ${timeStr}`;
}

function formatOdds(odds) {
    if (odds === null || odds === undefined) return '-';
    return Number(odds).toFixed(2);
}

function shortenMatchName(match) {
    if (!match) return '';
    const parts = match.split(' vs ');
    if (parts.length !== 2) return match;
    const getLastWord = (team) => {
        const words = team.trim().split(' ');
        return words[words.length - 1];
    };
    return `${getLastWord(parts[0])} vs ${getLastWord(parts[1])}`;
}

function formatLineWithSelection(line, selection) {
    if (!line) return '-';
    const prefix = selection === 'OVER' ? 'o' : selection === 'UNDER' ? 'u' : '';
    return `${prefix}${line}`;
}

function BookmakerLogo({ bookmaker, size = 'w-5 h-5' }) {
    const logo = BOOKMAKER_LOGOS[bookmaker];
    const displayName = BOOKMAKER_DISPLAY_NAMES[bookmaker] || bookmaker;

    if (logo) {
        return (
            <img
                src={logo}
                alt={displayName}
                className={`${size} rounded object-cover`}
                title={displayName}
            />
        );
    }
    return <span className="text-xs text-slate-400" title={displayName}>{displayName}</span>;
}

function BookmakerLink({ bookmaker, urls, children }) {
    const url = urls?.[bookmaker];
    if (url) {
        return (
            <a href={url} target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
                {children}
            </a>
        );
    }
    return children;
}

// Sorted list of known bookmakers for consistent column ordering
const KNOWN_BOOKMAKERS = ['fanduel', 'draftkings', 'pinnacle', 'sportsbet', 'bet365', 'tab', 'pointsbet', 'betright', 'betmakers', 'neds', 'betmgm', 'dabble'];

const HIGHLIGHT_DURATION = 8000;

function getBetKey(bet, strategy) {
    if (strategy === 'player_line') {
        return `${bet.player}|${bet.match}|${bet.marketType}|${bet.line}|${bet.selection}`;
    }
    if (strategy === 'arbs') {
        return `${bet.player}|${bet.match}|${bet.marketType}|${bet.line}`;
    }
    return `${bet.player}|${bet.match}|${bet.market}`;
}

function computeChanges(prevBets, newBets, strategy) {
    if (!prevBets || prevBets.length === 0) return { changes: {}, newKeys: new Set() };

    const prevMap = {};
    prevBets.forEach(bet => {
        prevMap[getBetKey(bet, strategy)] = bet;
    });

    const changes = {};
    const newKeys = new Set();

    newBets.forEach(bet => {
        const key = getBetKey(bet, strategy);
        const prev = prevMap[key];

        if (!prev) {
            newKeys.add(key);
            return;
        }

        const betChanges = {};

        if (strategy === 'player_line') {
            if (prev.expectedValue !== bet.expectedValue) {
                betChanges.expectedValue = bet.expectedValue > prev.expectedValue ? 'up' : 'down';
            }
            if (prev.bestOdds !== bet.bestOdds) {
                betChanges.bestOdds = bet.bestOdds > prev.bestOdds ? 'up' : 'down';
            }
            if (prev.line !== bet.line) {
                betChanges.line = bet.line > prev.line ? 'up' : 'down';
            }
            if (prev.avgFairOdds !== bet.avgFairOdds) {
                betChanges.avgFairOdds = bet.avgFairOdds > prev.avgFairOdds ? 'up' : 'down';
            }
            if (prev.impliedProbability !== bet.impliedProbability) {
                const prevNum = parseFloat(prev.impliedProbability);
                const newNum = parseFloat(bet.impliedProbability);
                if (!isNaN(prevNum) && !isNaN(newNum) && prevNum !== newNum) {
                    betChanges.impliedProbability = newNum > prevNum ? 'up' : 'down';
                }
            }

            const prevBkMap = {};
            prev.bookmakerData?.forEach(bd => { prevBkMap[bd.bookmaker] = bd; });
            bet.bookmakerData?.forEach(bd => {
                const prevBd = prevBkMap[bd.bookmaker];
                if (prevBd) {
                    const selKey = bet.selection === 'OVER' ? 'over_price' : 'under_price';
                    if (prevBd[selKey] !== bd[selKey]) {
                        betChanges[`bk_${bd.bookmaker}`] = bd[selKey] > prevBd[selKey] ? 'up' : 'down';
                    }
                }
            });
        }

        if (MV_STRATEGIES.has(strategy)) {
            const prevOpp = prev.valueOpportunities?.[0];
            const newOpp = bet.valueOpportunities?.[0];
            if (prevOpp && newOpp) {
                if (prevOpp.impliedEdgePercentage !== newOpp.impliedEdgePercentage) {
                    betChanges.edgePercentage = newOpp.impliedEdgePercentage > prevOpp.impliedEdgePercentage ? 'up' : 'down';
                }
                if (prevOpp.odds !== newOpp.odds) {
                    betChanges.bestOdds = newOpp.odds > prevOpp.odds ? 'up' : 'down';
                }
                if (prevOpp.avgMarketOdds !== newOpp.avgMarketOdds) {
                    betChanges.avgMarketOdds = newOpp.avgMarketOdds > prevOpp.avgMarketOdds ? 'up' : 'down';
                }
            }
            const prevBkOddsMap = {};
            prev.bookmakerOdds?.forEach(bo => { prevBkOddsMap[bo.bookmaker] = bo; });
            bet.bookmakerOdds?.forEach(bo => {
                const prevBo = prevBkOddsMap[bo.bookmaker];
                if (prevBo && prevBo.odds !== bo.odds) {
                    betChanges[`bk_${bo.bookmaker}`] = bo.odds > prevBo.odds ? 'up' : 'down';
                }
            });
        }

        if (strategy === 'arbs') {
            if (prev.arbPercentage !== bet.arbPercentage) {
                betChanges.arbPercentage = bet.arbPercentage > prev.arbPercentage ? 'up' : 'down';
            }
            if (prev.overOdds !== bet.overOdds) {
                betChanges.overOdds = bet.overOdds > prev.overOdds ? 'up' : 'down';
            }
            if (prev.underOdds !== bet.underOdds) {
                betChanges.underOdds = bet.underOdds > prev.underOdds ? 'up' : 'down';
            }
        }

        if (Object.keys(betChanges).length > 0) {
            changes[key] = betChanges;
        }
    });

    return { changes, newKeys };
}

function ChangeIndicator({ direction }) {
    if (direction === 'up') {
        return (
            <svg className="w-3 h-3 text-emerald-400 inline-block ml-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 15l7-7 7 7" />
            </svg>
        );
    }
    return (
        <svg className="w-3 h-3 text-red-400 inline-block ml-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M19 9l-7 7-7-7" />
        </svg>
    );
}

function getChangeColor(direction) {
    return direction === 'up' ? 'text-emerald-400' : 'text-red-400';
}

function OddsScreen() {
    const [sport, setSport] = useState('nba');
    const [strategy, setStrategy] = useState('player_line');
    const [oddsData, setOddsData] = useState(null);
    const [meta, setMeta] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'desc' });
    const [trackerModalBetKey, setTrackerModalBetKey] = useState(null);
    const [devig, setDevig] = useState('snipeaio');
    const [searchTerm, setSearchTerm] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [arbStake, setArbStake] = useState(100);
    const [evFilters, setEvFilters] = useState({
        minBookmakers: { enabled: true, value: 5 },
        minEv: { enabled: true, value: 3 },
        startWithin: { enabled: false, value: 3 },
        excludeBestBookmakers: { enabled: false, value: [] },
    });
    const [mvFilters, setMvFilters] = useState({
        minBookmakers: { enabled: true, value: 4 },
        minEv: { enabled: true, value: 10.5 },
        startWithin: { enabled: false, value: 3 },
        excludeBestBookmakers: { enabled: false, value: [] },
    });
    const [sbFilters, setSbFilters] = useState({
        minBookmakers: { enabled: false, value: 2 },
        minEv: { enabled: true, value: 10 },
        startWithin: { enabled: false, value: 3 },
        excludeBestBookmakers: { enabled: false, value: [] },
    });
    const [arbFilters, setArbFilters] = useState({
        minArb: { enabled: true, value: 2 },
        excludeBookmakers: { enabled: false, value: [] },
    });
    const filters = strategy === 'arbs' ? arbFilters : strategy === 'sb_baseline' ? sbFilters : strategy === 'market_value' ? mvFilters : evFilters;
    const setFilters = strategy === 'arbs' ? setArbFilters : strategy === 'sb_baseline' ? setSbFilters : strategy === 'market_value' ? setMvFilters : setEvFilters;
    const [showDevigged, setShowDevigged] = useState(true);
    const [countdown, setCountdown] = useState(30);
    const [changeMap, setChangeMap] = useState({});
    const [newBetKeys, setNewBetKeys] = useState(new Set());
    const [recentNewKeys, setRecentNewKeys] = useState(new Set());
    const [unitSize, setUnitSize] = useState(20);
    const [toast, setToast] = useState(null);
    const [isAddingBet, setIsAddingBet] = useState(false);
    const [mobileSettingsOpen, setMobileSettingsOpen] = useState(false);
    const countdownRef = useRef(null);
    const refreshIntervalRef = useRef(null);
    const prevDataRef = useRef(null);
    const highlightTimerRef = useRef(null);
    const isFirstLoad = useRef(true);

    const fetchOddsData = useCallback(async (selectedSport, selectedStrategy) => {
        setIsLoading(true);
        setError(null);

        const result = await oddsDataService.getOddsData(selectedSport, selectedStrategy);

        if (result.success) {
            const data = result.data;
            const bets = data?.bets || data?.arbs || [];

            if (!isFirstLoad.current && prevDataRef.current && bets.length > 0) {
                const { changes, newKeys } = computeChanges(prevDataRef.current, bets, selectedStrategy);

                setChangeMap(changes);
                setNewBetKeys(newKeys);
                setRecentNewKeys(newKeys);

                if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
                if (Object.keys(changes).length > 0 || newKeys.size > 0) {
                    highlightTimerRef.current = setTimeout(() => {
                        setChangeMap({});
                        setNewBetKeys(new Set());
                    }, HIGHLIGHT_DURATION);
                }
            } else {
                isFirstLoad.current = false;
            }

            prevDataRef.current = bets;

            if (bets.length > 0) {
                setOddsData(bets);
                setLastUpdated(data.lastUpdated);
            } else {
                setOddsData([]);
                setLastUpdated(data?.lastUpdated || null);
            }
            setMeta(result.meta || null);
        } else {
            setOddsData(null);
            setLastUpdated(null);
            setMeta(null);
            setError(result);
        }

        setIsLoading(false);
    }, []);

    useEffect(() => {
        fetchOddsData(sport, strategy);
        setCountdown(30);

        if (countdownRef.current) clearInterval(countdownRef.current);
        if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);

        countdownRef.current = setInterval(() => {
            setCountdown(prev => (prev <= 1 ? 30 : prev - 1));
        }, 1000);

        refreshIntervalRef.current = setInterval(() => {
            fetchOddsData(sport, strategy);
        }, 30000);

        return () => {
            clearInterval(countdownRef.current);
            clearInterval(refreshIntervalRef.current);
            if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
        };
    }, [sport, strategy, fetchOddsData]);

    useEffect(() => {
        const fetchSettings = async () => {
            const result = await resultsTrackerService.getSettings();
            if (result.success && result.data) {
                const { advancedUnitSettings, monthlyUnits, unitSize: defaultUnit } = result.data;
                if (advancedUnitSettings && monthlyUnits?.length > 0) {
                    const now = new Date();
                    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
                    const match = monthlyUnits
                        .filter(m => m.startDate?.substring(0, 7) <= currentMonth)
                        .sort((a, b) => b.startDate.localeCompare(a.startDate))[0];
                    setUnitSize(match?.unitSize || defaultUnit || 20);
                } else {
                    setUnitSize(defaultUnit || 20);
                }
            }
        };
        fetchSettings();
    }, []);

    const showToast = useCallback((message, type = 'error', duration = 5000) => {
        setToast({ message, type, duration });
    }, []);

    const handleAddToTracker = useCallback(async (betPayload) => {
        setIsAddingBet(true);
        const result = await resultsTrackerService.addBet(betPayload);
        if (result.success) {
            showToast('Bet added to tracker successfully', 'success');
            setTrackerModalBetKey(null);
        } else {
            if (result.isAuthError) {
                showToast('Session expired. Please log in again.', 'error');
            } else {
                showToast(result.message || 'Failed to add bet to tracker', 'error');
            }
        }
        setIsAddingBet(false);
        return result;
    }, [showToast]);

    const handleSportChange = (e) => {
        const newSport = e.target.value;
        setSport(newSport);
        setSortConfig({ key: null, direction: 'desc' });
        prevDataRef.current = null;
        isFirstLoad.current = true;
        setChangeMap({});
        setNewBetKeys(new Set());
        setRecentNewKeys(new Set());
    };

    const handleStrategyChange = (e) => {
        const newStrategy = e.target.value;
        setStrategy(newStrategy);
        setSortConfig({ key: null, direction: 'desc' });
        prevDataRef.current = null;
        isFirstLoad.current = true;
        setChangeMap({});
        setNewBetKeys(new Set());
        setRecentNewKeys(new Set());
    };

    const handleRefresh = () => {
        fetchOddsData(sport, strategy);
        setCountdown(30);

        if (countdownRef.current) clearInterval(countdownRef.current);
        if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);

        countdownRef.current = setInterval(() => {
            setCountdown(prev => (prev <= 1 ? 30 : prev - 1));
        }, 1000);

        refreshIntervalRef.current = setInterval(() => {
            fetchOddsData(sport, strategy);
        }, 30000);
    };

    const handleSort = useCallback((key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
        }));
    }, []);

    // Build list of all bookmakers across all bets for EV strategy (for dynamic columns)
    const allBookmakers = useMemo(() => {
        if (!oddsData || oddsData.length === 0) return [];

        const bookmakerSet = new Set();
        oddsData.forEach(bet => {
            if (strategy === 'player_line' && bet.bookmakerData) {
                bet.bookmakerData.forEach(b => bookmakerSet.add(b.bookmaker));
            } else if (MV_STRATEGIES.has(strategy) && bet.bookmakerOdds) {
                bet.bookmakerOdds.forEach(b => bookmakerSet.add(b.bookmaker));
            } else if (strategy === 'arbs') {
                if (bet.overBookmaker) bookmakerSet.add(bet.overBookmaker);
                if (bet.underBookmaker) bookmakerSet.add(bet.underBookmaker);
            }
        });

        return KNOWN_BOOKMAKERS.filter(b => bookmakerSet.has(b)).concat(
            [...bookmakerSet].filter(b => !KNOWN_BOOKMAKERS.includes(b)).sort()
        );
    }, [oddsData, strategy]);

    const maxBookmakers = useMemo(() => {
        if (!oddsData || oddsData.length === 0) return KNOWN_BOOKMAKERS.length;
        let max = 0;
        oddsData.forEach(bet => {
            const count = strategy === 'player_line'
                ? (bet.bookmakerData?.length || 0)
                : (bet.bookmakerOdds?.length || 0);
            if (count > max) max = count;
        });
        return Math.max(max, 2);
    }, [oddsData, strategy]);

    const trackerModalBet = useMemo(() => {
        if (!trackerModalBetKey || !oddsData) return null;
        return oddsData.find(bet => getBetKey(bet, strategy) === trackerModalBetKey) || null;
    }, [trackerModalBetKey, oddsData, strategy]);

    const getFairOdds = useCallback((bet) => {
        if (devig === 'snipeaio') return bet.avgFairOdds;

        const comboBooks = COMBO_DEVIG_BOOKMAKERS[devig];
        if (comboBooks) {
            const bkEntries = comboBooks
                .map(bk => bet.bookmakerData?.find(bd => bd.bookmaker === bk))
                .filter(Boolean);
            if (bkEntries.length === 0) return null;
            const isOver = bet.selection === 'OVER';
            const probs = bkEntries.map(bd => {
                const devPrice = isOver ? bd.devigged_over_price : bd.devigged_under_price;
                return devPrice && devPrice > 0 ? 1 / devPrice : null;
            }).filter(p => p !== null);
            if (probs.length === 0) return null;
            const avgProb = probs.reduce((sum, p) => sum + p, 0) / probs.length;
            return avgProb > 0 ? 1 / avgProb : null;
        }

        const bkData = bet.bookmakerData?.find(bd => bd.bookmaker === devig);
        if (!bkData) return null;
        return bet.selection === 'OVER' ? bkData.devigged_over_price : bkData.devigged_under_price;
    }, [devig]);

    const getBestBook = useCallback((bet) => {
        if (!bet.bookmakerData || bet.bookmakerData.length === 0) {
            return { bookmaker: bet.bestBookmaker, odds: bet.bestOdds };
        }
        const comboBooks = COMBO_DEVIG_BOOKMAKERS[devig];
        const excludeSet = comboBooks ? new Set(comboBooks) : null;
        let best = { bookmaker: null, odds: 0 };
        bet.bookmakerData.forEach(bd => {
            if (US_BOOKMAKERS.has(bd.bookmaker)) return;
            if (devig !== 'snipeaio') {
                if (excludeSet ? excludeSet.has(bd.bookmaker) : bd.bookmaker === devig) return;
            }
            const price = bet.selection === 'OVER' ? bd.over_price : bd.under_price;
            if (price && price > best.odds) {
                best = { bookmaker: bd.bookmaker, odds: price };
            }
        });
        if (!best.bookmaker) {
            return { bookmaker: bet.bestBookmaker, odds: bet.bestOdds };
        }
        return best;
    }, [devig]);

    const getEv = useCallback((bet) => {
        if (devig === 'snipeaio') return bet.expectedValue;
        const fairOdds = getFairOdds(bet);
        const { odds: bestOdds } = getBestBook(bet);
        if (!fairOdds || fairOdds <= 0 || !bestOdds) return null;
        return (bestOdds / fairOdds - 1) * 100;
    }, [devig, getFairOdds, getBestBook]);

    const filteredData = useMemo(() => {
        if (!oddsData || oddsData.length === 0) return [];
        let data = oddsData;

        if (strategy === 'player_line') {
            if (filters.minBookmakers.enabled) {
                data = data.filter(bet => (bet.bookmakerData?.length || 0) >= filters.minBookmakers.value);
            }
            if (filters.minEv.enabled) {
                data = data.filter(bet => (getEv(bet) ?? 0) >= filters.minEv.value);
            }
            if (filters.startWithin.enabled) {
                const cutoff = Date.now() + filters.startWithin.value * 60 * 60 * 1000;
                data = data.filter(bet => {
                    if (!bet.startTime) return false;
                    const startMs = new Date(bet.startTime).getTime();
                    return startMs <= cutoff && startMs >= Date.now();
                });
            }
            if (filters.excludeBestBookmakers.enabled && filters.excludeBestBookmakers.value.length > 0) {
                data = data.filter(bet => !filters.excludeBestBookmakers.value.includes(getBestBook(bet).bookmaker));
            }

            if (devig !== 'snipeaio') {
                const comboBooks = COMBO_DEVIG_BOOKMAKERS[devig];
                if (comboBooks) {
                    data = data.filter(bet => {
                        const bkMap = {};
                        bet.bookmakerData?.forEach(bd => { bkMap[bd.bookmaker] = bd; });
                        return comboBooks.every(bk => {
                            const bd = bkMap[bk];
                            if (!bd) return false;
                            const price = bet.selection === 'OVER' ? bd.devigged_over_price : bd.devigged_under_price;
                            return price && price > 0;
                        });
                    });
                } else {
                    data = data.filter(bet => {
                        const bd = bet.bookmakerData?.find(b => b.bookmaker === devig);
                        if (!bd) return false;
                        const price = bet.selection === 'OVER' ? bd.devigged_over_price : bd.devigged_under_price;
                        return price && price > 0;
                    });
                }
            }
        }

        if (MV_STRATEGIES.has(strategy)) {
            if (filters.minBookmakers.enabled) {
                data = data.filter(bet => (bet.bookmakerOdds?.length || 0) >= filters.minBookmakers.value);
            }
            if (filters.minEv.enabled) {
                data = data.filter(bet => Math.round((bet.valueOpportunities?.[0]?.impliedEdgePercentage ?? 0) * 10) / 10 >= filters.minEv.value);
            }
            if (filters.startWithin.enabled) {
                const cutoff = Date.now() + filters.startWithin.value * 60 * 60 * 1000;
                data = data.filter(bet => {
                    if (!bet.startTime) return false;
                    const startMs = new Date(bet.startTime).getTime();
                    return startMs <= cutoff && startMs >= Date.now();
                });
            }
            if (filters.excludeBestBookmakers.enabled && filters.excludeBestBookmakers.value.length > 0) {
                data = data.filter(bet => {
                    const bestBk = bet.valueOpportunities?.[0]?.bookmaker;
                    return !filters.excludeBestBookmakers.value.includes(bestBk);
                });
            }
        }

        if (strategy === 'arbs') {
            if (filters.minArb?.enabled) {
                data = data.filter(bet => (bet.arbPercentage ?? 0) >= filters.minArb.value);
            }
            if (filters.excludeBookmakers?.enabled && filters.excludeBookmakers.value.length > 0) {
                data = data.filter(bet =>
                    !filters.excludeBookmakers.value.includes(bet.overBookmaker) &&
                    !filters.excludeBookmakers.value.includes(bet.underBookmaker)
                );
            }
        }

        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            data = data.filter(bet => bet.player?.toLowerCase().includes(term));
        }

        const seen = new Set();
        data = data.filter(bet => {
            const key = getBetKey(bet, strategy);
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });

        return data;
    }, [oddsData, filters, strategy, getEv, getBestBook, searchTerm, devig]);

    // Sort bets
    const sortedBets = useMemo(() => {
        if (!filteredData || filteredData.length === 0) return [];
        if (!sortConfig.key) {
            if (strategy === 'player_line') {
                return [...filteredData].sort((a, b) => (getEv(b) ?? 0) - (getEv(a) ?? 0));
            }
            if (MV_STRATEGIES.has(strategy)) {
                return [...filteredData].sort((a, b) =>
                    (b.valueOpportunities?.[0]?.impliedEdgePercentage ?? 0) - (a.valueOpportunities?.[0]?.impliedEdgePercentage ?? 0)
                );
            }
            if (strategy === 'arbs') {
                return [...filteredData].sort((a, b) => (b.arbPercentage ?? 0) - (a.arbPercentage ?? 0));
            }
            return filteredData;
        }

        return [...filteredData].sort((a, b) => {
            let aVal, bVal;

            if (strategy === 'player_line') {
                switch (sortConfig.key) {
                    case 'expectedValue': aVal = getEv(a) ?? 0; bVal = getEv(b) ?? 0; break;
                    case 'bestOdds': aVal = getBestBook(a).odds ?? 0; bVal = getBestBook(b).odds ?? 0; break;
                    case 'player': aVal = a.player || ''; bVal = b.player || ''; break;
                    case 'match': aVal = a.match || ''; bVal = b.match || ''; break;
                    case 'startTime': aVal = a.startTime || ''; bVal = b.startTime || ''; break;
                    case 'fairOdds': aVal = getFairOdds(a) ?? 0; bVal = getFairOdds(b) ?? 0; break;
                    default: return 0;
                }
            } else if (MV_STRATEGIES.has(strategy)) {
                switch (sortConfig.key) {
                    case 'match': aVal = a.match || ''; bVal = b.match || ''; break;
                    case 'player': aVal = a.player || ''; bVal = b.player || ''; break;
                    case 'market': aVal = a.market || ''; bVal = b.market || ''; break;
                    case 'startTime': aVal = a.startTime || ''; bVal = b.startTime || ''; break;
                    case 'edgePercentage':
                        aVal = a.valueOpportunities?.[0]?.impliedEdgePercentage ?? 0;
                        bVal = b.valueOpportunities?.[0]?.impliedEdgePercentage ?? 0;
                        break;
                    case 'bestOdds':
                        aVal = a.valueOpportunities?.[0]?.odds ?? 0;
                        bVal = b.valueOpportunities?.[0]?.odds ?? 0;
                        break;
                    case 'avgMarketOdds':
                        aVal = a.valueOpportunities?.[0]?.avgMarketOdds ?? 0;
                        bVal = b.valueOpportunities?.[0]?.avgMarketOdds ?? 0;
                        break;
                    default: return 0;
                }
            } else if (strategy === 'arbs') {
                switch (sortConfig.key) {
                    case 'arbPercentage': aVal = a.arbPercentage ?? 0; bVal = b.arbPercentage ?? 0; break;
                    case 'match': aVal = a.match || ''; bVal = b.match || ''; break;
                    case 'player': aVal = a.player || ''; bVal = b.player || ''; break;
                    case 'line': aVal = a.line ?? 0; bVal = b.line ?? 0; break;
                    case 'overOdds': aVal = a.overOdds ?? 0; bVal = b.overOdds ?? 0; break;
                    case 'underOdds': aVal = a.underOdds ?? 0; bVal = b.underOdds ?? 0; break;
                    case 'startTime': aVal = a.startTime || ''; bVal = b.startTime || ''; break;
                    default: return 0;
                }
            } else {
                switch (sortConfig.key) {
                    default: return 0;
                }
            }

            if (typeof aVal === 'string') {
                const cmp = aVal.localeCompare(bVal);
                return sortConfig.direction === 'asc' ? cmp : -cmp;
            }

            return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
        });
    }, [filteredData, sortConfig, strategy, getFairOdds, getEv, getBestBook]);

    const SortIcon = ({ column }) => {
        if (sortConfig.key !== column) {
            return (
                <svg className="w-3 h-3 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                </svg>
            );
        }
        return sortConfig.direction === 'asc' ? (
            <svg className="w-3 h-3 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
            </svg>
        ) : (
            <svg className="w-3 h-3 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
        );
    };

    // EV (player_line) columns
    const renderEVTable = () => {
        const columns = [
            { key: 'expectedValue', label: 'EV%', width: '', sortable: true },
            { key: 'bestOdds', label: 'Best Book', width: '', sortable: true },
            { key: 'match', label: 'Game', width: '', sortable: true },
            { key: 'player', label: 'Player', width: '', sortable: true },
            { key: 'marketTypeDisplay', label: 'Prop', width: '', sortable: false },
            { key: 'line', label: 'Line', width: '', sortable: false },
            { key: 'fairOdds', label: 'Fair Odds', width: '', sortable: true },
        ];

        // Add dynamic bookmaker columns
        const bookmakerColumns = allBookmakers.map(bk => ({
            key: `bk_${bk}`,
            bookmaker: bk,
            label: BOOKMAKER_DISPLAY_NAMES[bk] || bk,
            width: '',
            sortable: false,
            isBookmaker: true,
        }));

        return (
            <>
                <thead className="bg-slate-800 sticky top-0 z-10">
                    <tr>
                        {columns.map(col => (
                            <th
                                key={col.key}
                                onClick={() => col.sortable && handleSort(col.key)}
                                className={`px-2 py-3 text-[11px] font-semibold text-slate-400 uppercase text-left whitespace-nowrap ${col.sortable ? 'cursor-pointer hover:text-white' : ''} transition-colors`}
                            >
                                <div className="flex items-center gap-1">
                                    {col.label}
                                    {col.sortable && <SortIcon column={col.key} />}
                                </div>
                            </th>
                        ))}
                        {bookmakerColumns.map(col => {
                            const isDevig = devig !== 'snipeaio' && (COMBO_DEVIG_BOOKMAKERS[devig] ? COMBO_DEVIG_BOOKMAKERS[devig].includes(col.bookmaker) : col.bookmaker === devig);
                            return (
                                <th
                                    key={col.key}
                                    className={`px-0.5 py-3 text-center ${isDevig ? 'bg-emerald-500/10 border-x border-emerald-500/20' : ''}`}
                                    title={col.label}
                                >
                                    <div className="flex items-center justify-center">
                                        <BookmakerLogo bookmaker={col.bookmaker} size="w-5 h-5" />
                                    </div>
                                </th>
                            );
                        })}
                        <th
                            onClick={() => handleSort('startTime')}
                            className="px-2 py-3 text-[11px] font-semibold text-slate-400 uppercase text-left whitespace-nowrap cursor-pointer hover:text-white transition-colors"
                        >
                            <div className="flex items-center gap-1">
                                Start
                                <SortIcon column="startTime" />
                            </div>
                        </th>
                        <th className="px-2 py-3">
                        </th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-700/30">
                    {sortedBets.length === 0 ? (
                        <tr>
                            <td colSpan={columns.length + bookmakerColumns.length + 2} className="px-4 py-8 text-center text-slate-500">
                                No EV opportunities found.
                            </td>
                        </tr>
                    ) : (
                        sortedBets.map((bet, index) => {
                            const bookmakerDataMap = {};
                            bet.bookmakerData?.forEach(bd => {
                                bookmakerDataMap[bd.bookmaker] = bd;
                            });

                            const betKey = getBetKey(bet, 'player_line');
                            const betChanges = changeMap[betKey] || {};
                            const isNew = newBetKeys.has(betKey);
                            const wasRecentlyNew = recentNewKeys.has(betKey);

                            return (
                                <tr
                                    key={index}
                                    className={`transition-all duration-500 ${
                                        isNew
                                            ? 'bg-emerald-500/10 border-l-2 border-l-emerald-400'
                                            : 'hover:bg-slate-800/30'
                                    }`}
                                >
                                    {/* EV% */}
                                    <td className="px-2 py-3 whitespace-nowrap">
                                        {(() => {
                                            const ev = getEv(bet);
                                            const isNeg = ev != null && ev < 0;
                                            const evColor = isNeg ? 'text-red-400' : 'text-emerald-400';
                                            return (
                                                <span className={`text-sm font-semibold ${evColor}`}>
                                                    {ev != null ? `${ev >= 0 ? '+' : ''}${ev.toFixed(1)}%` : '-'}
                                                    {betChanges.expectedValue && <ChangeIndicator direction={betChanges.expectedValue} />}
                                                </span>
                                            );
                                        })()}
                                    </td>
                                    {/* Best Book (odds + logo + external link) */}
                                    {(() => {
                                        const { bookmaker: bestBk, odds: bestPrice } = getBestBook(bet);
                                        const bestBkChange = betChanges.bestOdds || betChanges[`bk_${bestBk}`];
                                        return (
                                            <td className="px-2 py-3 whitespace-nowrap">
                                                <div className="flex items-center gap-1.5">
                                                    <span className={`text-sm font-mono font-semibold ${bestBkChange ? getChangeColor(bestBkChange) : 'text-white'}`}>
                                                        {formatOdds(bestPrice)}
                                                        {bestBkChange && <ChangeIndicator direction={bestBkChange} />}
                                                    </span>
                                                    <BookmakerLogo bookmaker={bestBk} size="w-5 h-5" />
                                                    {wasRecentlyNew && (
                                                        <span className="px-1.5 py-0.5 bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 rounded text-[10px] font-bold uppercase tracking-wider">
                                                            New
                                                        </span>
                                                    )}
                                                    {bet.bookmakerUrls?.[bestBk] && (
                                                        <a
                                                            href={bet.bookmakerUrls[bestBk]}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-slate-500 hover:text-emerald-400 transition-colors"
                                                            title="Open market"
                                                        >
                                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                            </svg>
                                                        </a>
                                                    )}
                                                </div>
                                            </td>
                                        );
                                    })()}
                                    {/* Game (shortened, compact) */}
                                    <td className="px-2 py-3 text-sm text-white font-medium whitespace-nowrap">
                                        <div className="max-w-[90px] truncate" title={bet.match}>{shortenMatchName(bet.match)}</div>
                                    </td>
                                    {/* Player */}
                                    <td className="px-2 py-3 text-sm text-slate-300 whitespace-nowrap">
                                        <div className="max-w-[120px] truncate" title={bet.player}>{bet.player}</div>
                                    </td>
                                    {/* Prop */}
                                    <td className="px-2 py-3 text-sm text-slate-400 whitespace-nowrap">
                                        <div className="max-w-[80px] truncate" title={bet.marketTypeDisplay}>{bet.marketTypeDisplay}</div>
                                    </td>
                                    {/* Line (with selection merged, e.g. o5.5 / u8.5) */}
                                    <td className="px-2 py-3 whitespace-nowrap">
                                        <span className={`text-sm font-mono ${betChanges.line ? getChangeColor(betChanges.line) : 'text-slate-300'}`}>
                                            {formatLineWithSelection(bet.line, bet.selection)}
                                            {betChanges.line && <ChangeIndicator direction={betChanges.line} />}
                                        </span>
                                    </td>
                                    {/* Fair Odds */}
                                    <td className="px-2 py-3 whitespace-nowrap">
                                        <span className={`text-sm font-mono ${betChanges.avgFairOdds ? getChangeColor(betChanges.avgFairOdds) : 'text-slate-400'}`}>
                                            {formatOdds(getFairOdds(bet))}
                                            {betChanges.avgFairOdds && <ChangeIndicator direction={betChanges.avgFairOdds} />}
                                        </span>
                                    </td>
                                    {/* Dynamic bookmaker odds columns */}
                                    {allBookmakers.map(bk => {
                                        const bd = bookmakerDataMap[bk];
                                        const { bookmaker: currentBestBk } = getBestBook(bet);
                                        const isBest = bk === currentBestBk;
                                        const offeredPrice = bet.selection === 'OVER' ? bd?.over_price : bd?.under_price;
                                        const deviggedPrice = bet.selection === 'OVER' ? bd?.devigged_over_price : bd?.devigged_under_price;
                                        const bkChange = betChanges[`bk_${bk}`];
                                        const isDevig = devig !== 'snipeaio' && (COMBO_DEVIG_BOOKMAKERS[devig] ? COMBO_DEVIG_BOOKMAKERS[devig].includes(bk) : bk === devig);
                                        return (
                                            <td key={bk} className={`px-0.5 py-2 text-center ${isDevig ? 'bg-emerald-500/5 border-x border-emerald-500/20' : ''}`}>
                                                {bd ? (
                                                    <BookmakerLink bookmaker={bk} urls={bet.bookmakerUrls}>
                                                        <div className="flex flex-col items-center">
                                                            <span className={`text-sm font-mono leading-tight ${
                                                                bkChange
                                                                    ? getChangeColor(bkChange)
                                                                    : isBest ? 'text-emerald-400 font-semibold' : isDevig ? 'text-emerald-300' : 'text-slate-400'
                                                            }`}>
                                                                {formatOdds(offeredPrice)}
                                                                {bkChange && <ChangeIndicator direction={bkChange} />}
                                                            </span>
                                                            {showDevigged && deviggedPrice && (
                                                                <span className="text-[10px] font-mono text-slate-500 leading-tight mt-0.5">
                                                                    {formatOdds(deviggedPrice)}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </BookmakerLink>
                                                ) : (
                                                    <span className="text-sm text-slate-600">-</span>
                                                )}
                                            </td>
                                        );
                                    })}
                                    {/* Start */}
                                    <td className="px-2 py-3 text-sm text-slate-500 whitespace-nowrap">
                                        {formatStartTime(bet.startTime)}
                                    </td>
                                    {/* Action */}
                                    <td className="px-2 py-3">
                                        <button
                                            onClick={() => setTrackerModalBetKey(getBetKey(bet, 'player_line'))}
                                            className="px-2 py-1 bg-emerald-600/20 text-emerald-400 border border-emerald-500/30 rounded-lg text-xs font-medium hover:bg-emerald-600/30 transition-all whitespace-nowrap"
                                        >
                                            Add to Tracker
                                        </button>
                                    </td>
                                </tr>
                            );
                        })
                    )}
                </tbody>
            </>
        );
    };

    // Market Value table
    const renderMarketValueTable = () => {
        const columns = [
            { key: 'edgePercentage', label: 'Edge%', sortable: true },
            { key: 'bestOdds', label: 'Best Book', sortable: true },
            { key: 'match', label: 'Game', sortable: true },
            { key: 'player', label: 'Player', sortable: true },
            { key: 'market', label: 'Market', sortable: true },
            { key: 'avgMarketOdds', label: strategy === 'sb_baseline' ? 'SB Price' : 'Avg Price', sortable: true },
        ];

        let displayBookmakers = allBookmakers;
        if (strategy === 'sb_baseline') {
            const sbBookmakers = new Set(['sportsbet']);
            sortedBets.forEach(bet => {
                const bestOpp = bet.valueOpportunities?.[0];
                if (bestOpp?.bookmaker) sbBookmakers.add(bestOpp.bookmaker);
            });
            displayBookmakers = allBookmakers.filter(bk => sbBookmakers.has(bk));
        }

        const bookmakerColumns = displayBookmakers.map(bk => ({
            key: `bk_${bk}`,
            bookmaker: bk,
            label: BOOKMAKER_DISPLAY_NAMES[bk] || bk,
            isBookmaker: true,
        }));

        return (
            <>
                <thead className="bg-slate-800 sticky top-0 z-10">
                    <tr>
                        {columns.map(col => (
                            <th
                                key={col.key}
                                onClick={() => col.sortable && handleSort(col.key)}
                                className={`px-2 py-3 text-[11px] font-semibold text-slate-400 uppercase text-left whitespace-nowrap ${col.sortable ? 'cursor-pointer hover:text-white' : ''} transition-colors`}
                            >
                                <div className="flex items-center gap-1">
                                    {col.label}
                                    {col.sortable && <SortIcon column={col.key} />}
                                </div>
                            </th>
                        ))}
                        {bookmakerColumns.map(col => (
                            <th
                                key={col.key}
                                className="px-0.5 py-3 text-center"
                                title={col.label}
                            >
                                <div className="flex items-center justify-center">
                                    <BookmakerLogo bookmaker={col.bookmaker} size="w-5 h-5" />
                                </div>
                            </th>
                        ))}
                        <th
                            onClick={() => handleSort('startTime')}
                            className="px-2 py-3 text-[11px] font-semibold text-slate-400 uppercase text-left whitespace-nowrap cursor-pointer hover:text-white transition-colors"
                        >
                            <div className="flex items-center gap-1">
                                Start
                                <SortIcon column="startTime" />
                            </div>
                        </th>
                        <th className="px-2 py-3"></th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-700/30">
                    {sortedBets.length === 0 ? (
                        <tr>
                            <td colSpan={columns.length + bookmakerColumns.length + 2} className="px-4 py-8 text-center text-slate-500">
                                No market value opportunities found.
                            </td>
                        </tr>
                    ) : (
                        sortedBets.map((bet, index) => {
                            const opp = bet.valueOpportunities?.[0];
                            if (!opp) return null;

                            const bkOddsMap = {};
                            bet.bookmakerOdds?.forEach(bo => { bkOddsMap[bo.bookmaker] = bo; });

                            const betKey = getBetKey(bet, strategy);
                            const betChanges = changeMap[betKey] || {};
                            const isNew = newBetKeys.has(betKey);
                            const wasRecentlyNew = recentNewKeys.has(betKey);

                            const bestBk = opp.bookmaker;

                            return (
                                <tr
                                    key={index}
                                    className={`transition-all duration-500 ${
                                        isNew
                                            ? 'bg-emerald-500/10 border-l-2 border-l-emerald-400'
                                            : 'hover:bg-slate-800/30'
                                    }`}
                                >
                                    {/* Edge% */}
                                    <td className="px-2 py-3 whitespace-nowrap">
                                        <span className={`text-sm font-semibold ${betChanges.edgePercentage ? getChangeColor(betChanges.edgePercentage) : 'text-emerald-400'}`}>
                                            +{opp.impliedEdgePercentage?.toFixed(1)}%
                                            {betChanges.edgePercentage && <ChangeIndicator direction={betChanges.edgePercentage} />}
                                        </span>
                                    </td>
                                    {/* Best Book */}
                                    <td className="px-2 py-3 whitespace-nowrap">
                                        <div className="flex items-center gap-1.5">
                                            <span className={`text-sm font-mono font-semibold ${betChanges.bestOdds ? getChangeColor(betChanges.bestOdds) : 'text-white'}`}>
                                                {formatOdds(opp.odds)}
                                                {betChanges.bestOdds && <ChangeIndicator direction={betChanges.bestOdds} />}
                                            </span>
                                            <BookmakerLogo bookmaker={bestBk} size="w-5 h-5" />
                                            {wasRecentlyNew && (
                                                <span className="px-1.5 py-0.5 bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 rounded text-[10px] font-bold uppercase tracking-wider">
                                                    New
                                                </span>
                                            )}
                                            {bet.bookmakerUrls?.[bestBk] && (
                                                <a
                                                    href={bet.bookmakerUrls[bestBk]}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-slate-500 hover:text-emerald-400 transition-colors"
                                                    title="Open market"
                                                >
                                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                    </svg>
                                                </a>
                                            )}
                                        </div>
                                    </td>
                                    {/* Game */}
                                    <td className="px-2 py-3 text-sm text-white font-medium whitespace-nowrap">
                                        <div className="max-w-[90px] truncate" title={bet.match}>{shortenMatchName(bet.match)}</div>
                                    </td>
                                    {/* Player */}
                                    <td className="px-2 py-3 text-sm text-slate-300 whitespace-nowrap">
                                        <div className="max-w-[120px] truncate" title={bet.player}>{bet.player}</div>
                                    </td>
                                    {/* Market */}
                                    <td className="px-2 py-3 text-sm text-slate-400 whitespace-nowrap">
                                        {bet.market}
                                    </td>
                                    {/* Avg Price */}
                                    <td className="px-2 py-3 whitespace-nowrap">
                                        <span className={`text-sm font-mono ${betChanges.avgMarketOdds ? getChangeColor(betChanges.avgMarketOdds) : 'text-slate-400'}`}>
                                            {formatOdds(opp.avgMarketOdds)}
                                            {betChanges.avgMarketOdds && <ChangeIndicator direction={betChanges.avgMarketOdds} />}
                                        </span>
                                    </td>
                                    {/* Bookmaker odds columns */}
                                    {displayBookmakers.map(bk => {
                                        const bo = bkOddsMap[bk];
                                        const isBest = bk === bestBk;
                                        const bkChange = betChanges[`bk_${bk}`];
                                        return (
                                            <td key={bk} className="px-0.5 py-3 text-center">
                                                {bo ? (
                                                    <BookmakerLink bookmaker={bk} urls={bet.bookmakerUrls}>
                                                        <span className={`text-sm font-mono ${
                                                            bkChange
                                                                ? getChangeColor(bkChange)
                                                                : isBest ? 'text-emerald-400 font-semibold' : 'text-slate-400'
                                                        }`}>
                                                            {formatOdds(bo.odds)}
                                                            {bkChange && <ChangeIndicator direction={bkChange} />}
                                                        </span>
                                                    </BookmakerLink>
                                                ) : (
                                                    <span className="text-sm text-slate-600">-</span>
                                                )}
                                            </td>
                                        );
                                    })}
                                    {/* Start */}
                                    <td className="px-2 py-3 text-sm text-slate-500 whitespace-nowrap">
                                        {formatStartTime(bet.startTime)}
                                    </td>
                                    {/* Action */}
                                    <td className="px-2 py-3">
                                        <button
                                            onClick={() => setTrackerModalBetKey(getBetKey(bet, strategy))}
                                            className="px-2 py-1 bg-emerald-600/20 text-emerald-400 border border-emerald-500/30 rounded-lg text-xs font-medium hover:bg-emerald-600/30 transition-all whitespace-nowrap"
                                        >
                                            Add to Tracker
                                        </button>
                                    </td>
                                </tr>
                            );
                        })
                    )}
                </tbody>
            </>
        );
    };

    const calcArbStakes = useCallback((overOdds, underOdds, total) => {
        if (!overOdds || !underOdds || !total || total <= 0) return { overStake: 0, underStake: 0, profit: 0 };
        const invSum = 1 / overOdds + 1 / underOdds;
        const overStake = total * (1 / overOdds) / invSum;
        const underStake = total - overStake;
        const profit = overStake * overOdds - total;
        return { overStake, underStake, profit };
    }, []);

    const renderArbsTable = () => {
        const columns = [
            { key: 'arbPercentage', label: 'Arb%', sortable: true },
            { key: 'match', label: 'Game', sortable: true },
            { key: 'player', label: 'Player', sortable: true },
            { key: 'marketTypeDisplay', label: 'Prop', sortable: false },
            { key: 'line', label: 'Line', sortable: true },
            { key: 'overOdds', label: 'Over', sortable: true },
            { key: 'underOdds', label: 'Under', sortable: true },
            { key: 'overStake', label: 'Over Stake', sortable: false },
            { key: 'underStake', label: 'Under Stake', sortable: false },
            { key: 'profit', label: 'Profit', sortable: false },
            { key: 'startTime', label: 'Start', sortable: true },
        ];

        return (
            <>
                <thead className="bg-slate-800 sticky top-0 z-10">
                    <tr>
                        <th colSpan={columns.length + 1} className="px-2 py-2 border-b border-slate-700/30">
                            <div className="flex items-center gap-2">
                                <span className="text-[11px] text-slate-400 uppercase font-semibold">Total Stake</span>
                                <div className="relative">
                                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500 text-xs">$</span>
                                    <input
                                        type="number"
                                        min="1"
                                        step="1"
                                        value={arbStake}
                                        onChange={(e) => setArbStake(Math.max(1, Number(e.target.value) || 1))}
                                        className="pl-5 pr-2 py-1 w-24 bg-slate-900 border border-slate-700 rounded-lg text-sm text-white font-mono focus:outline-none focus:border-emerald-500 transition-colors"
                                    />
                                </div>
                                {[50, 100, 200, 500].map(v => (
                                    <button
                                        key={v}
                                        onClick={() => setArbStake(v)}
                                        className={`px-2 py-1 rounded-lg text-[11px] font-medium border transition-all ${
                                            arbStake === v
                                                ? 'bg-emerald-600/20 text-emerald-400 border-emerald-500/30'
                                                : 'bg-slate-900 text-slate-400 border-slate-700 hover:text-white hover:border-slate-600'
                                        }`}
                                    >
                                        ${v}
                                    </button>
                                ))}
                            </div>
                        </th>
                    </tr>
                    <tr>
                        {columns.map(col => (
                            <th
                                key={col.key}
                                onClick={() => col.sortable && handleSort(col.key)}
                                className={`px-2 py-3 text-[11px] font-semibold text-slate-400 uppercase text-left whitespace-nowrap ${col.sortable ? 'cursor-pointer hover:text-white' : ''} transition-colors`}
                            >
                                <div className="flex items-center gap-1">
                                    {col.label}
                                    {col.sortable && <SortIcon column={col.key} />}
                                </div>
                            </th>
                        ))}
                        <th className="px-2 py-3"></th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-700/30">
                    {sortedBets.length === 0 ? (
                        <tr>
                            <td colSpan={columns.length + 1} className="px-4 py-8 text-center text-slate-500">
                                No arbitrage opportunities found.
                            </td>
                        </tr>
                    ) : (
                        sortedBets.map((bet, index) => {
                            const betKey = getBetKey(bet, 'arbs');
                            const betChanges = changeMap[betKey] || {};
                            const isNew = newBetKeys.has(betKey);
                            const wasRecentlyNew = recentNewKeys.has(betKey);
                            const { overStake, underStake, profit } = calcArbStakes(bet.overOdds, bet.underOdds, arbStake);

                            return (
                                <tr
                                    key={index}
                                    className={`transition-all duration-500 ${
                                        isNew
                                            ? 'bg-emerald-500/10 border-l-2 border-l-emerald-400'
                                            : 'hover:bg-slate-800/30'
                                    }`}
                                >
                                    {/* Arb% */}
                                    <td className="px-2 py-3 whitespace-nowrap">
                                        <span className="text-sm font-semibold text-emerald-400">
                                            {bet.arbPercentage != null ? `${bet.arbPercentage.toFixed(2)}%` : '-'}
                                            {betChanges.arbPercentage && <ChangeIndicator direction={betChanges.arbPercentage} />}
                                        </span>
                                    </td>
                                    {/* Game */}
                                    <td className="px-2 py-3 text-sm text-white font-medium whitespace-nowrap">
                                        <div className="max-w-[110px] truncate" title={bet.match}>{shortenMatchName(bet.match)}</div>
                                    </td>
                                    {/* Player */}
                                    <td className="px-2 py-3 text-sm text-slate-300 whitespace-nowrap">
                                        <div className="max-w-[120px] truncate" title={bet.player}>{bet.player}</div>
                                    </td>
                                    {/* Prop */}
                                    <td className="px-2 py-3 text-sm text-slate-400 whitespace-nowrap">
                                        <div className="max-w-[90px] truncate" title={bet.marketTypeDisplay}>{bet.marketTypeDisplay}</div>
                                    </td>
                                    {/* Line */}
                                    <td className="px-2 py-3 whitespace-nowrap">
                                        <span className="text-sm font-mono text-slate-300">{bet.line}</span>
                                    </td>
                                    {/* Over (odds + bookmaker) */}
                                    <td className="px-2 py-3 whitespace-nowrap">
                                        <div className="flex flex-col gap-0.5">
                                            <div className="flex items-center gap-1.5">
                                                <span className="text-[10px] text-slate-500 uppercase font-medium">O</span>
                                                <span className={`text-sm font-mono font-semibold ${betChanges.overOdds ? getChangeColor(betChanges.overOdds) : 'text-white'}`}>
                                                    {formatOdds(bet.overOdds)}
                                                    {betChanges.overOdds && <ChangeIndicator direction={betChanges.overOdds} />}
                                                </span>
                                                <BookmakerLogo bookmaker={bet.overBookmaker} size="w-4 h-4" />
                                                {wasRecentlyNew && (
                                                    <span className="px-1.5 py-0.5 bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 rounded text-[10px] font-bold uppercase tracking-wider">
                                                        New
                                                    </span>
                                                )}
                                                {bet.bookmakerUrls?.[bet.overBookmaker] && (
                                                    <a
                                                        href={bet.bookmakerUrls[bet.overBookmaker]}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-slate-500 hover:text-emerald-400 transition-colors"
                                                        title="Open market"
                                                    >
                                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                        </svg>
                                                    </a>
                                                )}
                                            </div>
                                            {bet.overSource === 'prop' && bet.overSelection && (
                                                <div className="text-[10px] text-slate-500 truncate max-w-[120px]" title={bet.overSelection}>
                                                    {bet.overSelection}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    {/* Under (odds + bookmaker) */}
                                    <td className="px-2 py-3 whitespace-nowrap">
                                        <div className="flex items-center gap-1.5">
                                            <span className="text-[10px] text-slate-500 uppercase font-medium">U</span>
                                            <span className={`text-sm font-mono font-semibold ${betChanges.underOdds ? getChangeColor(betChanges.underOdds) : 'text-white'}`}>
                                                {formatOdds(bet.underOdds)}
                                                {betChanges.underOdds && <ChangeIndicator direction={betChanges.underOdds} />}
                                            </span>
                                            <BookmakerLogo bookmaker={bet.underBookmaker} size="w-4 h-4" />
                                            {bet.bookmakerUrls?.[bet.underBookmaker] && (
                                                <a
                                                    href={bet.bookmakerUrls[bet.underBookmaker]}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-slate-500 hover:text-emerald-400 transition-colors"
                                                    title="Open market"
                                                >
                                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                    </svg>
                                                </a>
                                            )}
                                        </div>
                                    </td>
                                    {/* Over Stake */}
                                    <td className="px-2 py-3 whitespace-nowrap">
                                        <span className="text-sm font-mono text-slate-300">${overStake.toFixed(2)}</span>
                                    </td>
                                    {/* Under Stake */}
                                    <td className="px-2 py-3 whitespace-nowrap">
                                        <span className="text-sm font-mono text-slate-300">${underStake.toFixed(2)}</span>
                                    </td>
                                    {/* Profit */}
                                    <td className="px-2 py-3 whitespace-nowrap">
                                        <span className="text-sm font-mono font-semibold text-emerald-400">
                                            +${profit.toFixed(2)}
                                        </span>
                                    </td>
                                    {/* Start */}
                                    <td className="px-2 py-3 text-sm text-slate-500 whitespace-nowrap">
                                        {formatStartTime(bet.startTime)}
                                    </td>
                                    {/* Action */}
                                    <td className="px-2 py-3">
                                        <button
                                            onClick={() => setTrackerModalBetKey(getBetKey(bet, 'arbs'))}
                                            className="px-2 py-1 bg-emerald-600/20 text-emerald-400 border border-emerald-500/30 rounded-lg text-xs font-medium hover:bg-emerald-600/30 transition-all whitespace-nowrap"
                                        >
                                            Add to Tracker
                                        </button>
                                    </td>
                                </tr>
                            );
                        })
                    )}
                </tbody>
            </>
        );
    };

    const betCount = oddsData?.length ?? 0;

    return (
        <div className="flex-1 p-2 md:p-6">
            {/* Main Card */}
            <div className="bg-slate-900/50 rounded-xl md:rounded-2xl border border-slate-700/50 overflow-hidden relative">
                {/* Loading overlay - only on first load or when no data exists */}
                {isLoading && !oddsData && (
                    <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm z-10 flex items-center justify-center rounded-2xl">
                        <div className="flex items-center gap-3 bg-slate-800 px-6 py-4 rounded-xl border border-slate-700 shadow-xl">
                            <div className="w-5 h-5 border-2 border-emerald-400/30 border-t-emerald-400 rounded-full animate-spin"></div>
                            <span className="text-white font-medium">Fetching odds data...</span>
                        </div>
                    </div>
                )}

                {/* Header */}
                <div className="p-3 md:p-6 border-b border-slate-700/50">
                    {/* Top row: title + stats (always visible) + mobile settings toggle */}
                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 flex-wrap min-w-0">
                            <h3 className="text-base md:text-lg font-semibold text-white whitespace-nowrap">
                                {STRATEGY_OPTIONS.find(s => s.value === strategy)?.label || 'Odds'}
                            </h3>
                            {meta?.tier && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                                    {meta.tier}
                                </span>
                            )}
                            {!isLoading && oddsData && (
                                <span className="text-xs md:text-sm text-slate-400">
                                    <span className="text-white font-medium">{oddsData.length}</span> found
                                </span>
                            )}
                            {!isLoading && lastUpdated && (
                                <span className="hidden md:flex text-xs text-slate-500 items-center gap-2">
                                    <span>Updated {new Date(lastUpdated).toLocaleTimeString()}</span>
                                    <span className="text-slate-600">•</span>
                                    <span className="text-emerald-500/70">Next refresh in <span className="font-mono">{countdown}s</span></span>
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            {/* Mobile: compact refresh countdown */}
                            {!isLoading && lastUpdated && (
                                <span className="md:hidden text-[10px] text-emerald-500/70 font-mono whitespace-nowrap">{countdown}s</span>
                            )}
                            {/* Mobile: refresh button always visible */}
                            <button
                                onClick={handleRefresh}
                                disabled={isLoading}
                                className="md:hidden p-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg hover:from-emerald-500 hover:to-teal-500 transition-all flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Refresh"
                            >
                                <svg className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                            </button>
                            {/* Mobile: settings toggle */}
                            <button
                                onClick={() => setMobileSettingsOpen(prev => !prev)}
                                className="md:hidden p-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-300 hover:text-white transition-colors flex items-center gap-1.5"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                                </svg>
                                <svg className={`w-3 h-3 transition-transform ${mobileSettingsOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* Desktop controls (always visible on md+) */}
                    <div className="hidden md:flex items-end justify-end gap-3 mt-4">
                        {strategy !== 'arbs' && (
                            <div className="flex flex-col gap-1">
                                <label className="text-[10px] text-white uppercase tracking-wider font-medium">Search</label>
                                <div className="relative">
                                    <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                    <input
                                        type="text"
                                        placeholder="Player name..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="pl-9 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 w-40"
                                    />
                                </div>
                            </div>
                        )}
                        {strategy === 'player_line' && (
                            <div className="flex flex-col gap-1">
                                <label className="text-[10px] text-white uppercase tracking-wider font-medium">Devig</label>
                                <select
                                    value={devig}
                                    onChange={(e) => setDevig(e.target.value)}
                                    className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white focus:outline-none focus:border-emerald-500"
                                >
                                    {DEVIG_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                        <div className="flex flex-col gap-1">
                            <label className="text-[10px] text-white uppercase tracking-wider font-medium">Sport</label>
                            <select
                                value={sport}
                                onChange={handleSportChange}
                                className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white focus:outline-none focus:border-emerald-500"
                            >
                                {SPORT_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="flex flex-col gap-1">
                            <label className="text-[10px] text-white uppercase tracking-wider font-medium">Strategy</label>
                            <select
                                value={strategy}
                                onChange={handleStrategyChange}
                                className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white focus:outline-none focus:border-emerald-500 max-w-[160px]"
                            >
                                {STRATEGY_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>
                        {(strategy === 'player_line' || MV_STRATEGIES.has(strategy) || strategy === 'arbs') && (
                            <button
                                onClick={() => setShowFilters(true)}
                                className={`px-4 py-2 border rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-all ${
                                    Object.entries(filters).some(([, f]) => f.enabled && (!Array.isArray(f.value) || f.value.length > 0))
                                        ? 'bg-emerald-600/10 border-emerald-500/30 text-emerald-400 hover:bg-emerald-600/20'
                                        : 'bg-slate-800 border-slate-700 text-slate-300 hover:text-white hover:border-slate-600'
                                }`}
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                                </svg>
                                Filters
                                {(() => {
                                    const count = Object.entries(filters).filter(([, f]) => f.enabled && (!Array.isArray(f.value) || f.value.length > 0)).length;
                                    return count > 0 ? (
                                        <span className="px-1.5 py-0.5 bg-emerald-500/20 text-emerald-400 rounded text-[10px] font-bold">
                                            {count}
                                        </span>
                                    ) : null;
                                })()}
                            </button>
                        )}
                        {strategy === 'player_line' && (
                            <button
                                onClick={() => setShowDevigged(prev => !prev)}
                                className={`px-3 py-2 border rounded-lg text-xs font-medium flex items-center gap-2 transition-all whitespace-nowrap ${
                                    showDevigged
                                        ? 'bg-emerald-600/10 border-emerald-500/30 text-emerald-400'
                                        : 'bg-slate-800 border-slate-700 text-slate-400 hover:text-white hover:border-slate-600'
                                }`}
                            >
                                <div className={`w-7 h-4 rounded-full relative transition-colors ${showDevigged ? 'bg-emerald-500' : 'bg-slate-600'}`}>
                                    <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${showDevigged ? 'left-3.5' : 'left-0.5'}`} />
                                </div>
                                Devigged
                            </button>
                        )}
                        <button
                            onClick={handleRefresh}
                            disabled={isLoading}
                            className="p-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg hover:from-emerald-500 hover:to-teal-500 transition-all flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Refresh"
                        >
                            <svg className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                        </button>
                    </div>

                    {/* Mobile collapsible controls */}
                    {mobileSettingsOpen && (
                        <div className="md:hidden mt-3 pt-3 border-t border-slate-700/50 grid grid-cols-2 gap-2.5">
                            <div className="flex flex-col gap-1">
                                <label className="text-[10px] text-white uppercase tracking-wider font-medium">Strategy</label>
                                <select
                                    value={strategy}
                                    onChange={handleStrategyChange}
                                    className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white focus:outline-none focus:border-emerald-500"
                                >
                                    {STRATEGY_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex flex-col gap-1">
                                <label className="text-[10px] text-white uppercase tracking-wider font-medium">Sport</label>
                                <select
                                    value={sport}
                                    onChange={handleSportChange}
                                    className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white focus:outline-none focus:border-emerald-500"
                                >
                                    {SPORT_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>
                            {strategy === 'player_line' && (
                                <div className="flex flex-col gap-1">
                                    <label className="text-[10px] text-white uppercase tracking-wider font-medium">Devig</label>
                                    <select
                                        value={devig}
                                        onChange={(e) => setDevig(e.target.value)}
                                        className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white focus:outline-none focus:border-emerald-500"
                                    >
                                        {DEVIG_OPTIONS.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            {strategy !== 'arbs' && (
                                <div className="flex flex-col gap-1">
                                    <label className="text-[10px] text-white uppercase tracking-wider font-medium">Search</label>
                                    <div className="relative">
                                        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                        </svg>
                                        <input
                                            type="text"
                                            placeholder="Player name..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="pl-9 pr-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 w-full"
                                        />
                                    </div>
                                </div>
                            )}
                            <div className="col-span-2 flex items-center gap-2">
                                {(strategy === 'player_line' || MV_STRATEGIES.has(strategy) || strategy === 'arbs') && (
                                    <button
                                        onClick={() => setShowFilters(true)}
                                        className={`flex-1 px-3 py-2 border rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-all ${
                                            Object.entries(filters).some(([, f]) => f.enabled && (!Array.isArray(f.value) || f.value.length > 0))
                                                ? 'bg-emerald-600/10 border-emerald-500/30 text-emerald-400 hover:bg-emerald-600/20'
                                                : 'bg-slate-800 border-slate-700 text-slate-300 hover:text-white hover:border-slate-600'
                                        }`}
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                                        </svg>
                                        Filters
                                        {(() => {
                                            const count = Object.entries(filters).filter(([, f]) => f.enabled && (!Array.isArray(f.value) || f.value.length > 0)).length;
                                            return count > 0 ? (
                                                <span className="px-1.5 py-0.5 bg-emerald-500/20 text-emerald-400 rounded text-[10px] font-bold">
                                                    {count}
                                                </span>
                                            ) : null;
                                        })()}
                                    </button>
                                )}
                                {strategy === 'player_line' && (
                                    <button
                                        onClick={() => setShowDevigged(prev => !prev)}
                                        className={`px-3 py-2 border rounded-lg text-xs font-medium flex items-center gap-2 transition-all whitespace-nowrap ${
                                            showDevigged
                                                ? 'bg-emerald-600/10 border-emerald-500/30 text-emerald-400'
                                                : 'bg-slate-800 border-slate-700 text-slate-400 hover:text-white hover:border-slate-600'
                                        }`}
                                    >
                                        <div className={`w-7 h-4 rounded-full relative transition-colors ${showDevigged ? 'bg-emerald-500' : 'bg-slate-600'}`}>
                                            <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${showDevigged ? 'left-3.5' : 'left-0.5'}`} />
                                        </div>
                                        Devigged
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Error State */}
                {error && !isLoading && (
                    <div className="flex items-center justify-center py-20 px-6">
                        <div className="flex flex-col items-center text-center max-w-sm">
                            {/* Icon */}
                            {error.isSubscriptionError ? (
                                <div className="w-14 h-14 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mb-4">
                                    <svg className="w-7 h-7 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                    </svg>
                                </div>
                            ) : error.isAuthError ? (
                                <div className="w-14 h-14 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center mb-4">
                                    <svg className="w-7 h-7 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                    </svg>
                                </div>
                            ) : error.errorCode === 'RATE_LIMIT_EXCEEDED' ? (
                                <div className="w-14 h-14 rounded-full bg-orange-500/10 border border-orange-500/20 flex items-center justify-center mb-4">
                                    <svg className="w-7 h-7 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                            ) : error.errorCode === 'SERVICE_UNAVAILABLE' ? (
                                <div className="w-14 h-14 rounded-full bg-slate-500/10 border border-slate-500/20 flex items-center justify-center mb-4">
                                    <svg className="w-7 h-7 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                            ) : (
                                <div className="w-14 h-14 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center mb-4">
                                    <svg className="w-7 h-7 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                            )}

                            {/* Title */}
                            <h4 className={`text-base font-semibold mb-1.5 ${
                                error.isSubscriptionError ? 'text-amber-400'
                                    : error.errorCode === 'RATE_LIMIT_EXCEEDED' ? 'text-orange-400'
                                    : error.errorCode === 'SERVICE_UNAVAILABLE' ? 'text-slate-300'
                                    : 'text-red-400'
                            }`}>
                                {error.isSubscriptionError ? 'Subscription Required' :
                                 error.isAuthError ? 'Authentication Failed' :
                                 error.errorCode === 'RATE_LIMIT_EXCEEDED' ? 'Too Many Requests' :
                                 error.errorCode === 'SERVICE_UNAVAILABLE' ? 'Service Unavailable' :
                                 error.errorCode === 'NETWORK_ERROR' ? 'Connection Error' :
                                 'Something Went Wrong'}
                            </h4>

                            {/* Message */}
                            <p className="text-sm text-slate-500 leading-relaxed">{error.message}</p>

                            {/* Action */}
                            {(error.errorCode === 'SERVICE_UNAVAILABLE' || error.errorCode === 'NETWORK_ERROR' || error.errorCode === 'SERVER_ERROR') && (
                                <button
                                    onClick={handleRefresh}
                                    className="mt-5 px-5 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white font-medium hover:bg-slate-700 hover:border-slate-600 transition-all flex items-center gap-2"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                    Try Again
                                </button>
                            )}
                            {error.errorCode === 'RATE_LIMIT_EXCEEDED' && (
                                <p className="mt-3 text-xs text-slate-600">Rate limit resets every 15 minutes</p>
                            )}
                        </div>
                    </div>
                )}

                {/* Table */}
                {!error && (
                    <div className="overflow-y-auto max-h-[calc(100vh-200px)] min-h-[calc(100vh-200px)] md:max-h-[calc(100vh-280px)] md:min-h-[calc(100vh-280px)] styled-scrollbar">
                        <table className="w-full">
                            {strategy === 'player_line' && renderEVTable()}
                            {MV_STRATEGIES.has(strategy) && renderMarketValueTable()}
                            {strategy === 'arbs' && renderArbsTable()}
                        </table>
                    </div>
                )}

                {/* Summary Footer */}
                {!error && !isLoading && oddsData && (
                    <div className="p-4 bg-slate-800/30 border-t border-slate-700/50">
                        <div className="flex flex-wrap items-center justify-between gap-4 text-sm">
                            <div className="text-slate-400">
                                Showing <span className="text-white font-medium">{sortedBets.length}</span>
                                {filteredData.length !== oddsData.length && (
                                    <span> of {oddsData.length}</span>
                                )} opportunities
                            </div>
                            <div className="flex items-center gap-4">
                                {lastUpdated && (
                                    <div className="text-slate-500 text-xs">
                                        Last updated: {new Date(lastUpdated).toLocaleTimeString()}
                                    </div>
                                )}
                                {meta?.tier && meta.tier !== 'ELITE' && (
                                    <div className="text-xs text-amber-400/80">
                                        Upgrade to see more opportunities
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Add to Tracker Modal */}
            {trackerModalBet && (
                <AddToTrackerModal
                    bet={trackerModalBet}
                    onClose={() => setTrackerModalBetKey(null)}
                    bookmakerLogos={BOOKMAKER_LOGOS}
                    computedEv={getEv(trackerModalBet)}
                    computedFairOdds={getFairOdds(trackerModalBet)}
                    computedBestBook={getBestBook(trackerModalBet)}
                    strategy={strategy}
                    arbStake={arbStake}
                    unitSize={unitSize}
                    onAddToTracker={handleAddToTracker}
                    isAddingBet={isAddingBet}
                    sport={sport}
                />
            )}

            {/* Filters Modal */}
            {showFilters && (
                <FiltersModal
                    filters={filters}
                    onApply={setFilters}
                    onClose={() => setShowFilters(false)}
                    maxBookmakers={maxBookmakers}
                    bookmakers={allBookmakers}
                    bookmakerLogos={BOOKMAKER_LOGOS}
                    bookmakerNames={BOOKMAKER_DISPLAY_NAMES}
                    strategy={strategy}
                />
            )}

            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    duration={toast.duration}
                    onClose={() => setToast(null)}
                />
            )}
        </div>
    );
}

export default OddsScreen;
